import React, { useState, useMemo, useEffect } from 'react';
import useApiContext from 'hooks/useApiContext';
import useRepo from 'hooks/useRepo';
import { T, M } from 'TOMA';
import s from './Crm.module.scss';
import routes from 'router/routes';
import { toast } from 'react-toastify';
import { CustomLink } from 'components/CustomLink';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { iProspectFile, eCrmProspectStatus } from 'services/Crm/Crm.type';

const columnHelper = createColumnHelper<iProspectFile>();

const STATUSES: eCrmProspectStatus[] = [
  'new',
  'contacted',
  'qualified',
  'proposal',
  'negotiation',
  'won',
  'lost',
];

function CrmDashboard(): JSX.Element {
  const { crmRepository, eventRepository } = useApiContext();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [reload, setReload] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { response: events = [], loading: eventsLoading } = useRepo(
    async () => await eventRepository.getEvents(),
    []
  );

  useEffect(() => {
    if (events && events.length > 0 && !selectedEventId) {
      setSelectedEventId(Number(events[0]?.id));
    }
  }, [events]);

  const {
    response: prospects = [],
    loading: prospectsLoading,
    error,
  } = useRepo(async () => {
    if (selectedEventId) {
      return await crmRepository.getProspectsByEvent(selectedEventId);
    }
    return [] as iProspectFile[];
  }, [selectedEventId, reload]);

  const handleStatusChange = async (
    prospectId: number,
    newStatus: eCrmProspectStatus
  ): Promise<void> => {
    try {
      await crmRepository.updateProspectStatus(prospectId, newStatus);
      toast.success(t('crm:prospect_updated'));
      setReload((e) => e + 1);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.company?.name, {
        id: 'company_name',
        header: t('crm:company_name'),
        cell: (info) => {
          const prospect = info.row.original;
          return (
            <CustomLink
              to={routes.crmProspectDetail.to({
                prospectId: prospect.id,
              })}
              color="info"
            >
              <strong>{info.getValue() ?? '-'}</strong>
            </CustomLink>
          );
        },
      }),
      columnHelper.accessor('status', {
        header: t('crm:status'),
        cell: (info) => {
          const prospect = info.row.original;
          return (
            <select
              value={info.getValue()}
              onChange={async (e) =>
                await handleStatusChange(
                  prospect.id,
                  e.target.value as eCrmProspectStatus
                )
              }
              className={s.tableStatusSelect}
            >
              {STATUSES.map((st) => (
                <option key={st} value={st}>
                  {t(`crm:status_${st}`)}
                </option>
              ))}
            </select>
          );
        },
      }),
      columnHelper.accessor('priority', {
        header: t('crm:priority'),
        cell: (info) => (
          <span className={s.priorityBadge} data-priority={info.getValue()}>
            {t(`crm:priority_${info.getValue()}`)}
          </span>
        ),
      }),
      columnHelper.accessor('source', {
        header: t('crm:source'),
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.accessor('estimated_surface', {
        header: t('crm:estimated_surface'),
        cell: (info) => {
          const v = info.getValue();
          return v != null ? `${String(v)} m\u00B2` : '-';
        },
      }),
      columnHelper.accessor('estimated_budget', {
        header: t('crm:estimated_budget'),
        cell: (info) => {
          const v = info.getValue();
          return v != null ? `${v.toLocaleString()} \u20AC` : '-';
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: t('crm:actions'),
        cell: ({ row }) => (
          <div className={s.tableActions}>
            <CustomLink
              to={routes.crmProspectDetail.to({
                prospectId: row.original.id,
              })}
              color="info"
            >
              <span className={s.actionBtn}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6b7280"
                  strokeWidth="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </span>
            </CustomLink>
          </div>
        ),
      }),
    ],
    [t, selectedEventId]
  );

  const data = useMemo(
    () =>
      prospects?.filter((p: iProspectFile) => {
        if (!globalFilter) return true;
        const search = globalFilter.toLowerCase();
        return (
          (p.company?.name?.toLowerCase().includes(search) ?? false) ||
          (p.source?.toLowerCase().includes(search) ?? false)
        );
      }) ?? [],
    [prospects, globalFilter]
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div>
      <T.Container>
        <div className={s.pageHeader}>
          <h1 className={s.pageTitle}>{t('crm:prospect_files')}</h1>
          <button
            className={s.btnPrimary}
            onClick={() =>
              navigate(
                `${routes.crmProspectNew.path}${
                  selectedEventId ? `?eventId=${selectedEventId}` : ''
                }`
              )
            }
          >
            {t('crm:new_prospect_btn')}
          </button>
        </div>

        <div className={s.toolbar}>
          <div className={s.toolbarGroup}>
            <span className={s.toolbarLabel}>{t('crm:select_event')}:</span>
            <select
              value={selectedEventId ?? ''}
              onChange={(e) => setSelectedEventId(Number(e.target.value))}
              className={s.selectInput}
            >
              {(events || []).map((event: any) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
          </div>
          <input
            type="text"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder={t('crm:search_placeholder')}
            className={s.searchInput}
          />
        </div>

        <M.Loading
          loading={prospectsLoading || eventsLoading}
          error={error}
          fullWiewport
        >
          {data.length > 0 ? (
            <div className={s.tableWrap}>
              <table className={s.Table}>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          style={{ cursor: 'pointer' }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {{
                            asc: ' \u2191',
                            desc: ' \u2193',
                          }[header.column.getIsSorted() as string] ?? null}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className={s.row}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          <div className={s.row__div}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            !prospectsLoading && (
              <div className={s.emptyState}>
                <div className={s.emptyIcon}>&#128203;</div>
                <p className={s.emptyTitle}>{t('crm:no_prospects')}</p>
              </div>
            )
          )}
        </M.Loading>
      </T.Container>
    </div>
  );
}

export default CrmDashboard;
